// ---------------------------------------------------------------------------
// StatusTag — colored badge for task / pick-list-item statuses used in the
// /pick-lists/:id detail tables. Maps the backend status string to a
// (tone, i18n key) pair and renders the shared StatusBadge component.
// ---------------------------------------------------------------------------

import { useTranslation } from 'react-i18next';
import { StatusBadge, type BadgeTone } from '../StatusBadge';

type TaskItemStatus =
  | 'pending'
  | 'loc_ok'
  | 'confirm_ok'
  | 'done'
  | 'partial'
  | 'cancelled'
  | 'error'
  | string;

interface StatusTagProps {
  status: TaskItemStatus;
  /**
   * When `true` and `status='pending'`, render the badge as "Da assegnare"
   * instead of "In coda". This is the case for tasks whose mission has no
   * operator yet (release-to-pool state).
   */
  unassigned?: boolean;
}

const MAP: Record<string, { tone: BadgeTone; i18nKey: string }> = {
  pending:    { tone: 'neutral', i18nKey: 'task_status.pending_queued' },
  loc_ok:     { tone: 'info',    i18nKey: 'task_status.loc_ok' },
  confirm_ok: { tone: 'info',    i18nKey: 'task_status.confirm_ok' },
  done:       { tone: 'success', i18nKey: 'task_status.done' },
  partial:    { tone: 'warn',    i18nKey: 'task_status.partial' },
  cancelled:  { tone: 'danger',  i18nKey: 'task_status.cancelled' },
  error:      { tone: 'danger',  i18nKey: 'task_status.error' },
};

export function StatusTag({ status, unassigned }: StatusTagProps) {
  const { t } = useTranslation('picklist');
  if (status === 'pending' && unassigned) {
    return <StatusBadge tone="primary">{t('task_status.pending_unassigned')}</StatusBadge>;
  }
  const meta = MAP[status];
  if (!meta) return <StatusBadge tone="neutral">{status}</StatusBadge>;
  return <StatusBadge tone={meta.tone}>{t(meta.i18nKey)}</StatusBadge>;
}
